import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import SearchRoundedIcon       from "@mui/icons-material/SearchRounded";
import DownloadRoundedIcon     from "@mui/icons-material/DownloadRounded";
import OpenInNewRoundedIcon    from "@mui/icons-material/OpenInNewRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon        from "@mui/icons-material/ImageRounded";
import DescriptionRoundedIcon  from "@mui/icons-material/DescriptionRounded";
import TableChartRoundedIcon   from "@mui/icons-material/TableChartRounded";
import SlideshowRoundedIcon    from "@mui/icons-material/SlideshowRounded";
import FolderZipRoundedIcon    from "@mui/icons-material/FolderZipRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { formatFileSize as humanSize } from "../../../../util/numberFormat";

import { mailApi } from "../../api/mailApi";
import { saveAttachmentToDisk } from "../../util/attachmentDownload";
import { notify } from "../../../../util/toast";
import ComboBox from "../../../../component/common/ComboBox";
import dayjs from "dayjs";
import LoadingDots from "../../../../component/common/LoadingDots";

const PAGE_SIZE = 30;

/** 확장자별 아이콘 — 한눈에 파일 종류 식별. 색상은 도메인 친화적으로. */
function FileTypeIcon({ filename, sx }) {
    const ext = String(filename || "").toLowerCase().split(".").pop();
    const map = {
        pdf:  { Icon: PictureAsPdfRoundedIcon, color: "#dc2626" },
        png:  { Icon: ImageRoundedIcon,        color: "#0ea5e9" },
        jpg:  { Icon: ImageRoundedIcon,        color: "#0ea5e9" },
        jpeg: { Icon: ImageRoundedIcon,        color: "#0ea5e9" },
        gif:  { Icon: ImageRoundedIcon,        color: "#0ea5e9" },
        webp: { Icon: ImageRoundedIcon,        color: "#0ea5e9" },
        doc:  { Icon: DescriptionRoundedIcon,  color: "#2563eb" },
        docx: { Icon: DescriptionRoundedIcon,  color: "#2563eb" },
        hwp:  { Icon: DescriptionRoundedIcon,  color: "#2563eb" },
        xls:  { Icon: TableChartRoundedIcon,   color: "#16a34a" },
        xlsx: { Icon: TableChartRoundedIcon,   color: "#16a34a" },
        csv:  { Icon: TableChartRoundedIcon,   color: "#16a34a" },
        ppt:  { Icon: SlideshowRoundedIcon,    color: "#ea580c" },
        pptx: { Icon: SlideshowRoundedIcon,    color: "#ea580c" },
        zip:  { Icon: FolderZipRoundedIcon,    color: "#7c3aed" },
        rar:  { Icon: FolderZipRoundedIcon,    color: "#7c3aed" },
        "7z": { Icon: FolderZipRoundedIcon,    color: "#7c3aed" },
    };
    const m = map[ext] || { Icon: InsertDriveFileRoundedIcon, color: "#64748b" };
    const Icon = m.Icon;
    return <Icon sx={{ color: m.color, ...sx }} />;
}


const FOLDER_OPTIONS = [
    { value: "",        label: "전체 (휴지통/스팸 제외)" },
    { value: "INBOX",   label: "받은편지함" },
    { value: "SENT",    label: "보낸편지함" },
    { value: "DRAFT",   label: "임시보관함" },
    { value: "ARCHIVE", label: "보관함" },
    { value: "TRASH",   label: "휴지통" },
    { value: "SPAM",    label: "스팸" },
];

/**
 * 첨부 파일 라이브러리 — 본인이 접근 가능한 모든 메일의 첨부를 한 화면에서 검색·페이징.
 *
 * <h3>핵심 UX</h3>
 * <ul>
 *   <li>검색바 — 파일명·메일제목·발신자명·발신주소 통합 검색 (debounce 350ms)</li>
 *   <li>폴더 필터 — 받은편지함 / 보낸편지함 / 휴지통 등</li>
 *   <li>날짜 범위 — 시작·종료 일시</li>
 *   <li>각 행에서 다운로드 / 메일 열기 액션</li>
 *   <li>"더 보기" 버튼 페이징</li>
 * </ul>
 *
 * @param onOpenMail {(mailId: number) => void} — 메일 상세 모달 열기 콜백 (부모가 처리)
 */
export default function AttachmentLibraryPanel({ onOpenMail }) {
    const [q, setQ]               = useState("");
    const [folder, setFolder]     = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo]     = useState("");

    const [rows, setRows]         = useState([]);
    const [page, setPage]         = useState(0);
    const [totalElements, setTotal] = useState(0);
    const [loading, setLoading]   = useState(false);
    const [hasMore, setHasMore]   = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    // 0~100. null 이면 진행률을 모르는 상태(Content-Length 없음) — 라벨만 "받는 중..." 으로 둔다.
    const [downloadPct, setDownloadPct] = useState(null);

    // 필터 변경 → 디바운스 후 token++ 로 강제 refetch.
    // page 만 보고 fetch 하면, 이미 page===0 일 때 setPage(0) 가 no-op 이라
    // setRows([]) 만 적용돼 결과가 비워진 상태로 굳어버리는 버그 (마운트 직후 잠깐 보였다 사라지는 증상).
    const [reloadToken, setReloadToken] = useState(0);

    /** 검색·필터 변경 시 첫 페이지부터 다시 로드 (debounce). */
    const reloadTimerRef = useRef(null);
    const triggerReload = useCallback(() => {
        if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = setTimeout(() => {
            setPage(0);
            setRows([]);
            setHasMore(true);
            setReloadToken(t => t + 1);
        }, 350);
    }, []);

    // 첫 마운트에서는 fetch effect 가 page=0 으로 이미 호출되므로 중복/취소 방지를 위해 스킵.
    const isFirstMount = useRef(true);
    useEffect(() => {
        if (isFirstMount.current) { isFirstMount.current = false; return; }
        triggerReload();
    }, [q, folder, dateFrom, dateTo, triggerReload]);

    /** page 또는 reloadToken 이 바뀌면 fetch (page=0 이면 replace, 이외 append). */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = {
                    q: q.trim(),
                    folder,
                    page,
                    size: PAGE_SIZE,
                };
                // 날짜는 ISO 로 (datetime-local 의 "YYYY-MM-DDTHH:mm" 그대로 + ":00")
                if (dateFrom) params.dateFrom = dateFrom + (dateFrom.length === 16 ? ":00" : "");
                if (dateTo)   params.dateTo   = dateTo   + (dateTo.length   === 16 ? ":00" : "");

                const res = await mailApi.listMyAttachments(params);
                if (cancelled) return;
                const data = res?.data ?? res ?? {};
                const content = Array.isArray(data.content) ? data.content : [];
                setRows(prev => page === 0 ? content : [...prev, ...content]);
                setTotal(Number(data.totalElements ?? content.length));
                setHasMore(!data.last && content.length > 0);
            } catch (e) {
                if (!cancelled) {
                    notify(e?.message || "첨부 라이브러리 로딩 실패", "error");
                    setHasMore(false);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, reloadToken]);

    const handleDownload = useCallback(async (row) => {
        // 진행률·중복 클릭 차단·에러 문구는 메일 상세와 같은 규칙을 쓴다(util/attachmentDownload).
        // 진행 표시가 한 벌뿐이라 동시 다운로드는 받지 않는다 — 받는 중엔 다른 행도 무시.
        if (downloadingId != null) return;
        setDownloadingId(row.attachmentId);
        setDownloadPct(null);
        try {
            await saveAttachmentToDisk(row.attachmentId, row.filename, setDownloadPct);
        } catch (e) {
            notify(e?.message || "다운로드에 실패했습니다.", "error");
        } finally {
            setDownloadingId(null);
            setDownloadPct(null);
        }
    }, [downloadingId]);

    const totalLabel = useMemo(() => {
        if (loading && rows.length === 0) return "조회 중...";
        if (totalElements === 0) return "결과 없음";
        return `총 ${totalElements.toLocaleString()}건`;
    }, [loading, rows.length, totalElements]);

    return (
        <Box sx={{ p: 2.5, flex: 1, overflow: "auto", bgcolor: "#fafbfc" }}>
            {/* 헤더 */}
            <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>
                내 첨부 파일
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#64748b", mb: 2 }}>
                받았거나 보낸 메일의 모든 첨부를 한 곳에서 검색하고 다운로드합니다.
            </Typography>

            {/* 검색 + 필터 */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} sx={{ mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="파일명·메일 제목·발신자 검색"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    sx={{ flex: 1, minWidth: 300, bgcolor: "white" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ minWidth: 300, bgcolor: "white" }}>
                    <ComboBox clearable={false}
                        options={FOLDER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                        value={folder}
                        onChange={(v) => setFolder(v)} />
                </Box>
                <TextField
                    size="small"
                    type="datetime-local"
                    label="시작"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ bgcolor: "white", minWidth: 200 }}
                />
                <TextField
                    size="small"
                    type="datetime-local"
                    label="종료"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ bgcolor: "white", minWidth: 200 }}
                />
            </Stack>

            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1 }}>{totalLabel}</Typography>

            {/* 결과 리스트 */}
            <Stack spacing={0} sx={{ bgcolor: "white", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                {rows.length === 0 && !loading && (
                    <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
                        <Typography sx={{ fontSize: 13.5 }}>해당 조건의 첨부 파일이 없습니다.</Typography>
                    </Box>
                )}
                {rows.map((row, i) => (
                    <Box
                        key={`${row.attachmentId}-${i}`}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            borderBottom: i < rows.length - 1 ? "1px solid #f1f5f9" : "none",
                            "&:hover": { bgcolor: "#f8fafc" },
                        }}
                    >
                        <FileTypeIcon filename={row.filename} sx={{ fontSize: 32 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 13.5,
                                    fontWeight: 600,
                                    color: "#0f172a",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={row.filename}
                            >
                                {row.filename}
                                <Typography component="span" sx={{ fontSize: 11.5, color: "#94a3b8", ml: 1, fontWeight: 400 }}>
                                    {humanSize(row.sizeBytes)}
                                </Typography>
                                {row.folder && row.folder !== "INBOX" && (
                                    <Chip
                                        size="small"
                                        label={row.folder}
                                        sx={{ ml: 1, height: 17, fontSize: 10, fontWeight: 600,
                                              bgcolor: "#f1f5f9", color: "#475569" }}
                                    />
                                )}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "#475569",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    mt: 0.3,
                                }}
                                title={row.mailSubject}
                            >
                                <Box component="span"
                                     sx={{ color: "#2563eb", cursor: "pointer",
                                           "&:hover": { textDecoration: "underline" } }}
                                     onClick={() => onOpenMail?.(row.mailId)}>
                                    {row.mailSubject || "(제목 없음)"}
                                </Box>
                                <Typography component="span" sx={{ fontSize: 11.5, color: "#94a3b8", ml: 1 }}>
                                    · {row.fromName || row.fromAddr || "(발신자 없음)"}
                                </Typography>
                                <Typography component="span" sx={{ fontSize: 11.5, color: "#94a3b8", ml: 1 }}>
                                    · {row.occurredAt ? dayjs(row.occurredAt).format("YYYY-MM-DD HH:mm") : ""}
                                </Typography>
                            </Typography>
                        </Box>
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<OpenInNewRoundedIcon />}
                            onClick={() => onOpenMail?.(row.mailId)}
                            sx={{ whiteSpace: "nowrap", color: "#475569" }}
                        >
                            메일 열기
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadRoundedIcon />}
                            onClick={() => handleDownload(row)}
                            disabled={downloadingId === row.attachmentId}
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            {downloadingId === row.attachmentId
                                ? (downloadPct == null ? "받는 중..." : `받는 중 ${downloadPct}%`)
                                : "다운로드"}
                        </Button>
                    </Box>
                ))}
            </Stack>

            {/* 더 보기 */}
            {hasMore && rows.length > 0 && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                    <Button
                        variant="text"
                        onClick={() => setPage(p => p + 1)}
                        disabled={loading}
                    >
                        {loading ? <LoadingDots/> : "더 보기"}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
